import React from 'react';
import {
  View,
  StyleSheet,
  ImageSourcePropType,
  Image,
  Pressable,
  ViewStyle,
  ImageStyle,
  TextStyle,
} from 'react-native';
import {Formik} from 'formik';
import {Button, TextInput, Text} from 'ui-kits';
import colors from 'theme/Colors';
import {spacing, corner_radius} from 'theme/Units';
import {ResetPasswordValidation, ForgotPasswordValidation} from 'utils/schema';
import {useResponsiveStyles} from 'hooks/useResponsiveStyles';
import {checkPasswordUpdated} from 'redux/selectors/authSelector';
import {useSelector} from 'react-redux';

type propTypes = Readonly<{
  header: string;
  image?: ImageSourcePropType;
  setModalOpen?: (value: boolean) => void;
  mode: 'forgot' | 'reset';
  initialValues: {
    current_password: string;
    new_password: string;
    confirm_password: string;
  };

  onSubmit: (values: any) => void;
  onCancel: ()=>void;
  headerStyle?: TextStyle;
}>;

export function ResetPassword(props: propTypes) {
  const {
    header,
    image,
    setModalOpen,
    initialValues,
    onSubmit,
    onCancel,
    mode,
    headerStyle,
  } = props;


  const isPasswordUpdated = useSelector(checkPasswordUpdated);



  const handleCancel = () => {
    navigation.navigate(Routes.VIEW_PROFILE);
  };

  const styles = useResponsiveStyles<styleTypes>(baseStyles, responsive);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={mode === 'reset' ? ResetPasswordValidation : ForgotPasswordValidation}
      onSubmit={onSubmit}>
      {({
        handleChange,
        handleBlur,
        handleSubmit,
        values,
        errors,
        dirty,
        isValid,
      }) => (
        <View style={styles.container}>
          {image && (
            <Image resizeMode="cover" style={styles.image} source={image} />
          )}
          <Text style={[styles.header, headerStyle]} variant="header2">
            {header}
          </Text>
          <View style={styles.form}>
            {mode === 'reset' ? (
              <TextInput
                label={'Current Password'}
                onBlur={handleBlur('current_password')}
                onChangeText={handleChange('current_password')}
                size={'md'}
                value={values.current_password}
                error={errors.current_password}
                type={'password'}
                placeholder={'Enter current password'}
              />
            ) : null}

            <TextInput
              label={'New Password'}
              onBlur={handleBlur('new_password')}
              onChangeText={handleChange('new_password')}
              size={'md'}
              value={values.new_password}
              error={errors.new_password}
              type={'password'}
              placeholder={'Enter new password'}
            />

            <TextInput
              label={'Confirm New Password'}
              onBlur={handleBlur('confirm_password')}
              onChangeText={handleChange('confirm_password')}
              size={'md'}
              value={values.confirm_password}
              error={errors.confirm_password}
              type={'password'}
              placeholder={'Confirm New password'}
            />
            <View style={styles.actionButtonWrapper}>
              <Button
                style={styles.button}
                onPress={handleSubmit}
                text="Reset Password"
                variant="primary"
                disabled={!isValid || !dirty}
              />

              {isPasswordUpdated ? (
                <>
                  <Button
                    style={styles.button}
                    text="Cancel"
                    variant="secondary"
                    onPress={onCancel}
                  />
                  {mode === 'reset' ? (
                    <Pressable onPress={() => setModalOpen(true)}>
                      <Text
                        variant={'sourcesans14'}
                        style={styles.forgotPasswordText}>
                        Forgot Password?
                      </Text>
                    </Pressable>
                  ) : null}
                </>
              ) : null}
            </View>
          </View>
        </View>
      )}
    </Formik>
  );
}

type styleTypes = {
  container: ViewStyle;
  form: ViewStyle;
  actionButtonWrapper: ViewStyle;
  button: ViewStyle;
  image: ImageStyle;
  header: TextStyle;
  forgotPasswordText: TextStyle;
};
const baseStyles = StyleSheet.create({
  container: {
    backgroundColor: colors.palette.white,
    paddingVertical: spacing['4'],
    paddingHorizontal: spacing['8'],
    maxWidth: 350,
    maxHeight: 'fit-content',
    shadowColor: 'rgba(158, 119, 237, 0.2)',
    shadowOffset: {width: 1, height: 6},
    shadowRadius: 12, // Blur radius
    borderRadius: corner_radius['rounded-md'],
    gap: spacing['1'],
    alignItems: 'center',
  },
  form: {
    flex: 1,
    gap: spacing['3'],
    paddingVertical: spacing['4'],
    maxWidth: 280,
  },
  actionButtonWrapper: {
    flexDirection: 'column',
    marginTop: spacing['2'],
    gap: spacing['2'],
    alignItems: 'center',
  },
  button: {
    width: 280,
  },
  image: {
    width: 130,
    height: 90,
    alignSelf: 'center',
  },
  header: {
    alignSelf: 'center',
  },
  forgotPasswordText: {
    color: colors.palette.blue600,
    marginTop: spacing['1'],
  },
});

const responsive: Record<number, Partial<styleTypes>> = {
  640: StyleSheet.create({
    container: {
      flexDirection: 'column',
      gap: spacing['6'],
    },
    image: {
      width: 100,
      height: 100,
    },
  }),
  480: StyleSheet.create({
    container: {
      flexDirection: 'column',
      gap: spacing['6'],
      shadowColor: 'transparent',
      shadowOffset: {width: 0, height: 0},
      shadowRadius: 0,
      borderRadius: 0,
    },
    image: {
      width: 80,
      height: 80,
    },
  }),
};
